import { Activity, LayoutGrid, Sparkles } from "lucide-react";
import { Link } from "react-router";

import { ExternalLinkText, InfoTile } from "@/components/common";
import { cn } from "@/lib/utils";
import { Badge, Button, Card, CardContent, CardHeader, CardTitle } from "@runcheetah/ui";
import { StatusBadge } from "@/pages/brands";

import type { BrandDetail, BrandStatus } from "./types";
import { getAutopilotSourceLabel } from "./autopilot";
import {
  ADMIN_ASSET_CREATION_FLOW_OPTIONS,
  getAssetCreationFlowDescription,
  getAssetCreationFlowLabel,
  getAssetCreationProvenanceLabel,
} from "./asset-creation";

const formatCreditUsage = (usage: BrandDetail["creditUsage"]): string => {
  return `${usage.used.toLocaleString()} used`;
};

export function BrandOverviewCard({
  brand,
  formatDate,
  autopilotEnabled,
  autopilotSource,
  isAutopilotPending,
  onToggleAutopilot,
  assetCreation,
  pendingAssetCreationFlow,
  isAssetCreationPending,
  onAssetCreationFlowChange,
}: {
  brand: BrandDetail;
  formatDate: (value: string) => string;
  autopilotEnabled: boolean;
  autopilotSource: "default" | "global" | "brand";
  isAutopilotPending: boolean;
  onToggleAutopilot: () => void;
  assetCreation: BrandDetail["assetCreation"];
  pendingAssetCreationFlow: BrandDetail["assetCreation"]["flow"] | null;
  isAssetCreationPending: boolean;
  onAssetCreationFlowChange: (flow: BrandDetail["assetCreation"]["flow"]) => void;
}) {
  return (
    <Card className="h-fit overflow-hidden">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 border-b bg-muted/20 pb-3">
        <CardTitle className="text-sm font-semibold">Overview</CardTitle>
        <Activity className="size-4 text-muted-foreground" />
      </CardHeader>
      <CardContent className="space-y-4 p-4">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
          <InfoTile
            label="Domain"
            value={brand.sourceDomain}
            valueClassName="truncate font-medium"
          />
          <InfoTile label="Status">
            <div className="mt-1">
              <StatusBadge status={brand.status as BrandStatus} />
            </div>
          </InfoTile>
        </div>
        <InfoTile label="Source URL" className="flex flex-col">
          <ExternalLinkText
            href={brand.sourceUrl}
            className="mt-1 flex w-fit max-w-full items-center gap-1 truncate text-sm text-blue-600 hover:underline"
            truncate
          >
            {brand.sourceUrl}
          </ExternalLinkText>
        </InfoTile>
        <InfoTile label="Owner" className="flex flex-col">
          {brand.ownerId ? (
            <Link
              to={`/users/${brand.ownerId}`}
              className="mt-0.5 text-sm font-medium hover:underline"
            >
              {brand.ownerName ? `${brand.ownerName} (${brand.ownerEmail})` : brand.ownerEmail}
            </Link>
          ) : (
            <span className="mt-0.5 text-sm text-muted-foreground">—</span>
          )}
        </InfoTile>
        <InfoTile
          label="Created At"
          value={formatDate(brand.createdAt)}
          className="flex flex-col"
        />
        <InfoTile label="Credits" value={formatCreditUsage(brand.creditUsage)} />
        <div className="rounded-xl border bg-muted/20 p-3">
          <div className="flex items-start justify-between gap-3">
            <div className="space-y-1">
              <p className="text-sm font-medium">Autopilot</p>
              <p className="text-xs leading-5 text-muted-foreground">
                Controls automatic campaign generation for this brand.
              </p>
            </div>
            <Badge variant={autopilotEnabled ? "default" : "secondary"}>
              {autopilotEnabled ? "On" : "Off"}
            </Badge>
          </div>
          <div className="mt-3 flex items-center justify-between gap-3">
            <span className="text-xs text-muted-foreground">
              {getAutopilotSourceLabel(autopilotSource)}
            </span>
            <Button
              type="button"
              size="sm"
              variant={autopilotEnabled ? "outline" : "default"}
              disabled={isAutopilotPending}
              onClick={onToggleAutopilot}
            >
              {isAutopilotPending ? "Saving..." : autopilotEnabled ? "Turn off" : "Turn on"}
            </Button>
          </div>
        </div>
        <div className="rounded-xl border bg-muted/20 p-3">
          <div className="flex items-start justify-between gap-3">
            <div className="space-y-1">
              <p className="text-sm font-medium">Asset creation flow</p>
              <p className="text-xs leading-5 text-muted-foreground">
                Controls how new single assets are planned. Carousel assets remain template-based.
              </p>
            </div>
            <Badge variant={assetCreation.flow === "freestyle" ? "default" : "secondary"}>
              {getAssetCreationFlowLabel(assetCreation.flow)}
            </Badge>
          </div>
          <div className="mt-3 rounded-lg bg-background/70 p-2 text-xs text-muted-foreground">
            <span className="font-medium text-foreground">Provenance:</span>{" "}
            {getAssetCreationProvenanceLabel(assetCreation)}
          </div>
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            {ADMIN_ASSET_CREATION_FLOW_OPTIONS.map((flow) => {
              const isSelected = flow === assetCreation.flow;
              const isPending = flow === pendingAssetCreationFlow;
              const Icon = flow === "freestyle" ? Sparkles : LayoutGrid;

              return (
                <button
                  key={flow}
                  type="button"
                  aria-pressed={isSelected}
                  disabled={isAssetCreationPending}
                  onClick={() => onAssetCreationFlowChange(flow)}
                  className={cn(
                    "rounded-lg border bg-background p-2 text-left transition disabled:cursor-not-allowed disabled:opacity-70",
                    isSelected || isPending
                      ? "border-primary bg-primary/5"
                      : "hover:border-primary/40 hover:bg-background/80",
                  )}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="flex items-center gap-1.5 text-sm font-medium">
                      <Icon className="size-3.5" aria-hidden="true" />
                      {getAssetCreationFlowLabel(flow)}
                    </span>
                    {isPending ? (
                      <span className="text-[11px] text-muted-foreground">Saving...</span>
                    ) : isSelected ? (
                      <span className="text-[11px] text-muted-foreground">Current</span>
                    ) : null}
                  </div>
                  <p className="mt-1 text-xs leading-5 text-muted-foreground">
                    {getAssetCreationFlowDescription(flow)}
                  </p>
                </button>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
